import { persianToEnglishDigits } from '@utilities/persianToEnglishDigits';

type FormatType =
  | 'yyyy/mm/dd'
  | 'day dd MM yyyy'
  | 'dd MM yyyy'
  | 'day MM yyyy'
  | 'yyyy/mm/dd - day';

export const convertToPersianDate = (
  rawDate: string | Date,
  options: { format?: FormatType } = { format: 'yyyy/mm/dd' },
): string | null => {
  const date = typeof rawDate === 'string' ? new Date(rawDate) : rawDate;

  if (isNaN(date?.getTime())) {
    return null;
  }

  if (options.format === 'yyyy/mm/dd - day') {
    const locale = 'fa-IR-u-ca-persian';
    const dtf = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = dtf.formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    const weekday = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
    }).format(date);

    if (!year || !month || !day || year.includes('‎−')) return null;

    const datePart = `${year}/${month}/${day}`.replace(/\s/g, '');
    const result = `${datePart} - ${weekday}`;
    return persianToEnglishDigits(result.trim().replace(/\s+/g, ' '));
  }

  const intlOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: options.format.includes('MM') ? 'long' : 'numeric',
    day: options.format.includes('dd') ? 'numeric' : '2-digit',
  };

  if (options.format.includes('day')) {
    intlOptions.weekday = 'long';
  }

  const formatter = new Intl.DateTimeFormat('fa-IR', intlOptions);
  const parts = formatter.formatToParts(date);

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  const weekday = parts.find((part) => part.type === 'weekday')?.value;

  if (!year || !month || !day || year.includes('‎−')) return null;

  let result = options.format
    .replace('yyyy', year)
    .replace('MM', month)
    .replace('mm', month.padStart(2, '0'))
    .replace('dd', day.padStart(2, '0'))
    .replace('day', weekday || '');

  return persianToEnglishDigits(result.trim().replace(/\s+/g, ' '));
};

export const formatBookingDateTime = (date?: string, timeSlot?: string) => {
  const formattedDate = convertToPersianDate(date ?? '');

  if (!formattedDate) return '-';
  if (!timeSlot) return formattedDate;
  const timeMatch = timeSlot.match(/(\d{1,2}:\d{2})/);
  const formattedTime = timeMatch?.[1] ?? timeSlot;

  return `${formattedDate} - ${formattedTime}`;
};
